import React from 'react';
import styled, { css } from 'styled-components';

/**
 * Shared field-rendering helpers for `patientDetails` and `customField`.
 * Not part of the block-per-file deliverable list, but both blocks share the
 * exact same "location-added field" shape (label/type/options/helpText/
 * required/showOnSummary/includeInEmail + optional `when` condition), so the
 * actual <input> rendering and conditional-visibility check live here once
 * rather than being copy-pasted — judgment call, kept in booking/ (not
 * registry.js/Announcement.jsx/icons/, which are owned by other work).
 */

/** @typedef {'short text'|'long text'|'dropdown'|'yes-no'|'date'|'number'|'file'} LocationFieldType */
/** @typedef {'text'|'email'|'tel'} CoreFieldType */

/**
 * Options for a field-type `select` in an inspector schema. Exported so both
 * blocks' `schema` arrays stay in sync with what FieldInput can render.
 */
export const FIELD_TYPE_OPTIONS = [
  { label: 'Short text', value: 'short text' },
  { label: 'Long text', value: 'long text' },
  { label: 'Dropdown', value: 'dropdown' },
  { label: 'Yes / No', value: 'yes-no' },
  { label: 'Date', value: 'date' },
  { label: 'Number', value: 'number' },
  { label: 'File upload', value: 'file' }
];

/**
 * Evaluates a field's `when` condition against `formValues`.
 * Per spec: if `formValues` isn't supplied (context not wired up yet), the
 * field always shows rather than disappearing in preview.
 *
 * @param {{fieldId: string, op: 'is'|'is not', value: unknown}|null|undefined} when
 * @param {Object.<string, unknown>} [formValues]
 * @returns {boolean}
 */
/**
 * A dropdown's options as plain strings. The inspector stores them as list
 * items (`{ value }`), older/hand-written configs as bare strings — either
 * way an empty one (still being typed) is left out.
 * @param {(string|{value?: string})[]} [options]
 * @returns {string[]}
 */
export function dropdownOptions(options) {
  return (Array.isArray(options) ? options : [])
    .map((option) => (typeof option === 'string' ? option : (option?.value ?? '')))
    .map((option) => String(option).trim())
    .filter(Boolean);
}

export function evaluateWhen(when, formValues) {
  if (!when || !when.fieldId) return true;
  if (!formValues) return true;
  const actual = formValues[when.fieldId];
  return when.op === 'is not' ? actual !== when.value : actual === when.value;
}

export const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 16px;
  font-family: var(--flow-font, inherit);
`;

export const FieldLabel = styled.label`
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--flow-text, #1a1a1a);
`;

export const RequiredMark = styled.span`
  color: #c0392b;
  margin-left: 2px;
`;

export const HelpText = styled.p`
  margin: 0;
  font-size: 0.8rem;
  color: var(--flow-text, #1a1a1a);
  opacity: 0.7;
`;

const controlStyles = `
  min-height: 44px;
  padding: 10px 12px;
  font-size: 0.95rem;
  font-family: var(--flow-font, inherit);
  color: var(--flow-text, #1a1a1a);
  background: var(--flow-surface, #fff);
  border: 1px solid #cfd4da;
  border-radius: var(--flow-radius, 8px);
  width: 100%;
  box-sizing: border-box;

  &:focus-visible {
    outline: 2px solid var(--flow-primary, #0d6efd);
    outline-offset: 1px;
  }
`;

export const TextInput = styled.input`
  ${controlStyles}
`;

export const TextArea = styled.textarea`
  ${controlStyles}
  min-height: 88px;
  resize: vertical;
`;

/**
 * Replaces the browser's dropdown arrow, which ignores padding and sits on
 * the rounded corner at larger theme radii, with a chevron inset from the edge.
 */
export const selectChevron = css`
  appearance: none;
  -webkit-appearance: none;
  padding-right: 40px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1.5l5 5 5-5' fill='none' stroke='%236c757d' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 16px center;
  background-size: 12px 8px;
`;

export const Select = styled.select`
  ${controlStyles}
  ${selectChevron}
`;

export const RadioGroup = styled.div`
  display: flex;
  gap: 20px;
`;

export const RadioOption = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  font-size: 0.95rem;
  color: var(--flow-text, #1a1a1a);

  input {
    width: 20px;
    height: 20px;
  }
`;

/**
 * Renders one form control for a "location-added field" (patientDetails
 * item or a standalone customField), or for one of patientDetails' three
 * core fields (fullName/email/phone — recognizable by `field.core`).
 *
 * Controlled if `value`/`onChange` are provided; otherwise renders
 * uncontrolled so the block still previews as interactive before a
 * FlowRenderer wires up real form state.
 *
 * @param {Object} props
 * @param {{id: string, label: string, type: LocationFieldType|CoreFieldType, options?: string[], helpText?: string, required?: boolean, core?: boolean}} props.field
 * @param {unknown} [props.value]
 * @param {(value: unknown) => void} [props.onChange]
 */
export function FieldInput({ field, value, onChange }) {
  const inputId = `flow-field-${field.id}`;
  const handleChange = (event) => {
    if (!onChange) return;
    const target = event.target;
    onChange(target.type === 'checkbox' ? target.checked : target.value);
  };

  if (field.type === 'yes-no') {
    return (
      <FieldWrapper as="fieldset" style={{ border: 'none', padding: 0 }}>
        <FieldLabel as="legend" style={{ padding: 0 }}>
          {field.label}
          {field.required && <RequiredMark aria-hidden="true">*</RequiredMark>}
        </FieldLabel>
        <RadioGroup role="radiogroup" aria-required={field.required || undefined}>
          {['Yes', 'No'].map((option) => (
            <RadioOption key={option}>
              <input
                type="radio"
                name={inputId}
                value={option}
                checked={value !== undefined ? value === option : undefined}
                onChange={handleChange}
              />
              {option}
            </RadioOption>
          ))}
        </RadioGroup>
        {field.helpText && <HelpText id={`${inputId}-help`}>{field.helpText}</HelpText>}
      </FieldWrapper>
    );
  }

  if (field.type === 'dropdown') {
    return (
      <FieldWrapper>
        <FieldLabel htmlFor={inputId}>
          {field.label}
          {field.required && <RequiredMark aria-hidden="true">*</RequiredMark>}
        </FieldLabel>
        <Select
          id={inputId}
          required={field.required}
          value={value ?? ''}
          onChange={handleChange}
          aria-describedby={field.helpText ? `${inputId}-help` : undefined}>
          <option value="" disabled>
            Select an option
          </option>
          {dropdownOptions(field.options).map((option, index) => (
            <option key={`${index}-${option}`} value={option}>
              {option}
            </option>
          ))}
        </Select>
        {field.helpText && <HelpText id={`${inputId}-help`}>{field.helpText}</HelpText>}
      </FieldWrapper>
    );
  }

  if (field.type === 'long text') {
    return (
      <FieldWrapper>
        <FieldLabel htmlFor={inputId}>
          {field.label}
          {field.required && <RequiredMark aria-hidden="true">*</RequiredMark>}
        </FieldLabel>
        <TextArea
          id={inputId}
          required={field.required}
          value={value ?? ''}
          onChange={handleChange}
          aria-describedby={field.helpText ? `${inputId}-help` : undefined}
        />
        {field.helpText && <HelpText id={`${inputId}-help`}>{field.helpText}</HelpText>}
      </FieldWrapper>
    );
  }

  // short text | date | number | file | core text/email/tel
  const htmlType =
    field.type === 'short text' ? 'text' : field.type === 'file' ? 'file' : field.type; // 'date' | 'number' | 'text' | 'email' | 'tel' pass through as-is

  return (
    <FieldWrapper>
      <FieldLabel htmlFor={inputId}>
        {field.label}
        {field.required && <RequiredMark aria-hidden="true">*</RequiredMark>}
      </FieldLabel>
      <TextInput
        id={inputId}
        type={htmlType}
        required={field.required}
        {...(htmlType === 'file'
          ? { onChange: (e) => onChange && onChange(e.target.files) }
          : { value: value ?? '', onChange: handleChange })}
        aria-describedby={field.helpText ? `${inputId}-help` : undefined}
      />
      {field.helpText && <HelpText id={`${inputId}-help`}>{field.helpText}</HelpText>}
    </FieldWrapper>
  );
}
